import { ApiError, NetworkError } from '@/api/client';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { ChannelCard, ChannelKind, ChannelCheckDetail, ChannelState } from '@/api/types';

/** Marketplaces in the order the backend returns them. */
export const CHANNEL_KINDS: readonly ChannelKind[] = ['WB', 'OZON', 'YANDEX_MARKET'];

export type ChannelFieldName = 'apiKey' | 'clientId' | 'businessId' | 'campaignId';

export type ChannelFieldSpec = {
  readonly name: ChannelFieldName;
  readonly labelKey: TranslationKey;
  /** Secret fields are rendered as password inputs and cleared after every request. */
  readonly secret: boolean;
  readonly digits: boolean;
};

const API_KEY: ChannelFieldSpec = { name: 'apiKey', labelKey: 'integrations.field.apiKey', secret: true, digits: false };
const CLIENT_ID: ChannelFieldSpec = { name: 'clientId', labelKey: 'integrations.field.clientId', secret: false, digits: true };
const BUSINESS_ID: ChannelFieldSpec = { name: 'businessId', labelKey: 'integrations.field.businessId', secret: false, digits: true };
const CAMPAIGN_ID: ChannelFieldSpec = { name: 'campaignId', labelKey: 'integrations.field.campaignId', secret: false, digits: true };

/** The exact fields each marketplace needs, in display order. */
export const CHANNEL_FIELDS: Record<ChannelKind, readonly ChannelFieldSpec[]> = {
  WB: [API_KEY],
  OZON: [CLIENT_ID, API_KEY],
  YANDEX_MARKET: [API_KEY, BUSINESS_ID, CAMPAIGN_ID],
};

export const KIND_NAME_KEYS: Record<ChannelKind, TranslationKey> = {
  WB: 'integrations.kind.WB',
  OZON: 'integrations.kind.OZON',
  YANDEX_MARKET: 'integrations.kind.YANDEX_MARKET',
};

export const KIND_HELP_KEYS: Record<ChannelKind, TranslationKey> = {
  WB: 'integrations.help.WB',
  OZON: 'integrations.help.OZON',
  YANDEX_MARKET: 'integrations.help.YANDEX_MARKET',
};

export const STATE_TONE: Record<ChannelState, 'ok' | 'warn' | 'neutral'> = {
  CONNECTED: 'ok',
  NEEDS_ATTENTION: 'warn',
  UNVERIFIED: 'neutral',
  NOT_CONNECTED: 'neutral',
};

export const ATTENTION_KEYS: Record<ChannelCheckDetail, TranslationKey> = {
  OK: 'integrations.attention.DEGRADED',
  UNAUTHORISED: 'integrations.attention.UNAUTHORISED',
  RATE_LIMITED: 'integrations.attention.RATE_LIMITED',
  DOWN: 'integrations.attention.DOWN',
  DEGRADED: 'integrations.attention.DEGRADED',
};

/** "****ab12" -> "ab12". Never more than the last four characters, whatever the server sent. */
export function lastFour(maskedHint: string | null): string | null {
  if (!maskedHint) return null;
  const tail = maskedHint.replace(/\*/g, '').slice(-4);
  return tail || null;
}

export type FieldValues = Partial<Record<ChannelFieldName, string>>;

/** Client-side pre-check; the server still decides. Returns the first problem, or null. */
export function precheckFields(kind: ChannelKind, values: FieldValues): { field: ChannelFieldName; messageKey: TranslationKey } | null {
  for (const spec of CHANNEL_FIELDS[kind]) {
    const v = (values[spec.name] ?? '').trim();
    if (!v) return { field: spec.name, messageKey: 'integrations.err.required' };
    if (spec.digits && !/^\d+$/.test(v)) return { field: spec.name, messageKey: FIELD_KEYS[spec.name] };
  }
  return null;
}

/** The exact `fields` object the API expects - trimmed strings, nothing extra. */
export function buildFields(kind: ChannelKind, values: FieldValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const spec of CHANNEL_FIELDS[kind]) out[spec.name] = (values[spec.name] ?? '').trim();
  return out;
}

const FIELD_KEYS: Record<ChannelFieldName, TranslationKey> = {
  apiKey: 'integrations.err.field.apiKey',
  clientId: 'integrations.err.field.clientId',
  businessId: 'integrations.err.field.businessId',
  campaignId: 'integrations.err.field.campaignId',
};

export type ChannelProblem = {
  readonly messageKey: TranslationKey;
  /** The input to mark invalid, when the server pointed at one. */
  readonly field?: ChannelFieldName;
  /** The card is out of date (someone else connected / disconnected it) - reload the list. */
  readonly stale?: boolean;
};

/** Friendly message for any failure. Never includes the typed key, the error code or an id. */
export function describeChannelError(error: unknown): ChannelProblem {
  if (error instanceof NetworkError) return { messageKey: 'integrations.err.network' };
  if (!(error instanceof ApiError)) return { messageKey: 'integrations.err.generic' };
  switch (error.code) {
    case 'SELLER_CHANNEL.INPUT_INVALID': {
      const f = String(error.params.field ?? '');
      const field = f === 'apiKey' || f === 'clientId' || f === 'businessId' || f === 'campaignId' ? f : undefined;
      return { messageKey: (field ? FIELD_KEYS[field] : undefined) ?? 'integrations.err.inputInvalid', ...(field ? { field } : {}) };
    }
    case 'SELLER_CHANNEL.KEY_REJECTED':
      // Which fields to check depends on the marketplace (Ozon: key AND Client ID; Yandex Market: key, business and campaign IDs).
      return { messageKey: error.params.kind === 'WB' ? 'integrations.err.keyRejected.WB' : error.params.kind === 'OZON' ? 'integrations.err.keyRejected.OZON' : error.params.kind === 'YANDEX_MARKET' ? 'integrations.err.keyRejected.YANDEX_MARKET' : 'integrations.err.keyRejected' };
    case 'SELLER_CHANNEL.REQUEST_REJECTED':
      return { messageKey: error.params.kind === 'WB' ? 'integrations.err.requestRejected.WB' : error.params.kind === 'OZON' ? 'integrations.err.requestRejected.OZON' : error.params.kind === 'YANDEX_MARKET' ? 'integrations.err.requestRejected.YANDEX_MARKET' : 'integrations.err.requestRejected' };
    case 'SELLER_CHANNEL.RATE_LIMITED':
      return { messageKey: 'integrations.err.marketplaceRateLimited' };
    case 'SELLER_CHANNEL.UNAVAILABLE':
      return { messageKey: 'integrations.err.unavailable' };
    case 'SELLER_CHANNEL.NOT_VERIFIED':
      return { messageKey: 'integrations.err.notVerified' };
    case 'SELLER_CHANNEL.ALREADY_CONNECTED':
      return { messageKey: 'integrations.err.alreadyConnected', stale: true };
    case 'SELLER_CHANNEL.NOT_CONNECTED':
      return { messageKey: 'integrations.err.notConnected', stale: true };
    case 'SELLER_CHANNEL.CONTRACT_REQUIRED':
      return { messageKey: 'integrations.err.contractRequired' };
    case 'SELLER_CHANNEL.WAREHOUSE_REQUIRED':
      return { messageKey: error.params.reason === 'NONE' ? 'integrations.err.warehouseNone' : 'integrations.err.warehouseChoose' };
    case 'SELLER_REQUEST.OWNER_NOT_ALLOWED':
      return { messageKey: 'integrations.err.ownerNotAllowed' };
    case 'SELLER_REQUEST.WAREHOUSE_NOT_ALLOWED':
      return { messageKey: 'integrations.err.warehouseNotAllowed' };
    default:
      break;
  }
  if (error.status === 429 || error.code === 'RATE_LIMITED') return { messageKey: 'integrations.err.tooMany' };
  if (error.status === 413) return { messageKey: 'integrations.err.tooLarge' };
  if (error.status === 401) return { messageKey: 'integrations.err.session' };
  return { messageKey: 'integrations.err.generic' };
}

export function cardKey(ownerId: string, kind: ChannelKind): string {
  return `${ownerId}:${kind}`;
}

export function isStored(card: ChannelCard): boolean {
  return card.state !== 'NOT_CONNECTED' && card.channelId !== null;
}
